import { useForm as useFormHook } from "hooks";
import { converStringtoArray } from "util/transformers";
import { trimObjectValues } from "util/support";
import { toBooleanSwitch } from "util/casts";
import { BooleanSwitchValue } from "consts/general";
import {
    FormField,
    type SetupPaymentMethodBasicInfoForm,
    type SetupPaymentMethodBasicInfo
} from "./types";
import useValidationSchema from "./useValidationSchema";

export const formFields: readonly (keyof SetupPaymentMethodBasicInfo)[] = [
    FormField.ForCurrencies,
    FormField.Name,
    FormField.Tag,
    FormField.ForCountries,
    FormField.DayToHoldOfflineFunds,
    FormField.Provider,
    FormField.RequireSpecIn,
    FormField.SendPaymentReceiptMail,
    FormField.SendPaymentRequestMail,
    FormField.IsAssignable,
    FormField.IsRefundable
];

export const pickFormValues = <T extends Partial<SetupPaymentMethodBasicInfo>>(
    values: T
): Partial<SetupPaymentMethodBasicInfo> => {
    const out: Record<string, unknown> = {};
    for (const key of formFields) {
        if (key in values) {
            out[key] = (values as Record<string, unknown>)[key];
        }
    }
    return out as Partial<SetupPaymentMethodBasicInfo>;
};

export const getInitialValues = ({
    forCurrencies,
    forCountries,
    requireSpecIn,
    sendPaymentReceiptMail,
    sendPaymentRequestMail,
    isAssignable,
    isRefundable,
    ...restInitialValues
}: Partial<SetupPaymentMethodBasicInfo> = {}): SetupPaymentMethodBasicInfo => trimObjectValues({
    [FormField.ForCurrencies]: converStringtoArray(forCurrencies),
    [FormField.ForCountries]: converStringtoArray(forCountries),
    [FormField.Name]: '',
    [FormField.Tag]: '',
    [FormField.DayToHoldOfflineFunds]: 0,
    [FormField.Provider]: '',
    [FormField.RequireSpecIn]: toBooleanSwitch(requireSpecIn ?? BooleanSwitchValue.Off),
    [FormField.SendPaymentReceiptMail]: toBooleanSwitch(sendPaymentReceiptMail ?? BooleanSwitchValue.Off),
    [FormField.SendPaymentRequestMail]: toBooleanSwitch(sendPaymentRequestMail ?? BooleanSwitchValue.Off),
    [FormField.IsAssignable]: toBooleanSwitch(isAssignable ?? BooleanSwitchValue.Off),
    [FormField.IsRefundable]: toBooleanSwitch(isRefundable ?? BooleanSwitchValue.Off),
    ...restInitialValues
});

export const useForm = (setupPaymentMethodBasicInfoFormProps: SetupPaymentMethodBasicInfoForm) =>
    useFormHook({
        enableReinitialize: true,
        ...setupPaymentMethodBasicInfoFormProps,
        validateOnMount: true,
        validationSchema: useValidationSchema(),
        initialValues: getInitialValues(setupPaymentMethodBasicInfoFormProps.initialValues)
    });
